import { supabase } from './supabase'

// Congela o spread_congelado de todos os picks da semana usando o spread atual dos jogos.
// Chamada quando admin ativa "congelar spreads", garantindo que todos usem o mesmo valor.
export async function congelarSpreadsPicks(semanaId) {
  const { data: jogos } = await supabase
    .from('jogos').select('time_casa, time_visitante, spread_casa, spread_visitante').eq('semana_id', semanaId)
  if (!jogos?.length) return

  const { data: picks } = await supabase
    .from('picks').select('id, time_escolhido').eq('semana_id', semanaId).not('time_escolhido', 'is', null)
  if (!picks?.length) return

  for (const pick of picks) {
    const jogo = jogos.find(j => j.time_casa === pick.time_escolhido || j.time_visitante === pick.time_escolhido)
    if (!jogo) continue
    const spread = pick.time_escolhido === jogo.time_casa ? jogo.spread_casa : jogo.spread_visitante
    if (spread === null || spread === undefined) continue
    await supabase.from('picks').update({ spread_congelado: spread }).eq('id', pick.id)
  }
}

export async function processarResultadosDaSemana(semanaId) {
  try {
    const { data: jogos } = await supabase.from('jogos').select('*').eq('semana_id', semanaId)
    const { data: semanaInfo } = await supabase.from('semanas').select('liga_id, numero, tipo').eq('id', semanaId).single()
    const ligaId = semanaInfo?.liga_id

    const { data: membros } = await supabase
      .from('membros_liga').select('id, vidas_restantes').eq('liga_id', ligaId).eq('ativo', true).eq('aprovado', true).gt('vidas_restantes', 0)

    const { data: picks } = await supabase.from('picks').select('*').eq('semana_id', semanaId)

    const atualizacoes = []
    for (const membro of membros) {
      const pick = picks.find(p => p.membro_id === membro.id)
      if (pick?.vidas_antes != null) continue  // já processado — evita duplo processamento
      const vidasAntes = membro.vidas_restantes
      let resultado = 'nopick', vidasDepois = Math.max(0, vidasAntes - 1)

      if (pick?.time_escolhido) {
        const jogo = jogos?.find(j => j.time_casa === pick.time_escolhido || j.time_visitante === pick.time_escolhido)
        if (!jogo?.resultado) continue
        if (jogo.resultado === 'empate') { resultado = 'draw'; vidasDepois = Math.max(0, vidasAntes - 1) }
        else {
          const ganhou = (jogo.resultado === 'casa' && pick.time_escolhido === jogo.time_casa) ||
                         (jogo.resultado === 'visitante' && pick.time_escolhido === jogo.time_visitante)
          if (ganhou) { resultado = 'win'; vidasDepois = vidasAntes }
          else { resultado = 'loss'; vidasDepois = Math.max(0, vidasAntes - 1) }
        }
      }
      atualizacoes.push({ membroId: membro.id, pickId: pick?.id || null, resultado, vidasAntes, vidasDepois, semanaId })
    }

    for (const upd of atualizacoes) {
      if (upd.pickId) {
        await supabase.from('picks').update({ resultado: upd.resultado, vidas_antes: upd.vidasAntes, vidas_depois: upd.vidasDepois }).eq('id', upd.pickId)
      } else {
        await supabase.from('picks').upsert({ membro_id: upd.membroId, semana_id: semanaId, time_escolhido: null, resultado: 'nopick', vidas_antes: upd.vidasAntes, vidas_depois: upd.vidasDepois, confirmado_em: new Date().toISOString() }, { onConflict: 'membro_id,semana_id' })
      }
      await supabase.from('membros_liga').update({ vidas_restantes: upd.vidasDepois }).eq('id', upd.membroId)
      await supabase.from('historico_vidas').upsert({ membro_id: upd.membroId, semana_id: semanaId, vidas: upd.vidasDepois }, { onConflict: 'membro_id,semana_id' })
    }

    await supabase.from('semanas').update({ resultados_processados: true }).eq('id', semanaId)
    return { sucesso: true, processados: atualizacoes.length, atualizacoes }
  } catch (err) {
    return { sucesso: false, erro: err.message }
  }
}

export async function avancarParaProximaSemana(ligaId, semanaAtualNumero) {
  try {
    const proximoNumero = semanaAtualNumero + 1
    let tipo = 'regular'
    if (proximoNumero === 19) tipo = 'wildcard'
    else if (proximoNumero === 20) tipo = 'divisional'
    else if (proximoNumero === 21) tipo = 'conference'
    else if (proximoNumero === 22) tipo = 'superbowl'

    // Fecha a semana atual
    await supabase.from('semanas')
      .update({ picks_abertos: false })
      .eq('liga_id', ligaId).eq('numero', semanaAtualNumero)

    // Verifica se a próxima semana já existe (pré-criada pelo calendário)
    const { data: existente } = await supabase
      .from('semanas').select('*')
      .eq('liga_id', ligaId).eq('numero', proximoNumero).maybeSingle()

    if (existente) {
      const { error } = await supabase.from('semanas')
        .update({ picks_abertos: true, resultados_processados: false, picks_revelados: false, spreads_congelados: false })
        .eq('id', existente.id)
      if (error) throw error
      return { sucesso: true, semana: { ...existente, picks_abertos: true } }
    }

    // Semana não existe — cria nova com deadline na próxima quinta
    const agora = new Date()
    const diasParaQuinta = (4 - agora.getDay() + 7) % 7 || 7
    const proximaQuinta = new Date(agora)
    proximaQuinta.setDate(agora.getDate() + diasParaQuinta)
    proximaQuinta.setHours(0, 20, 0, 0) // 21h20 Brasília = 00h20 UTC

    const { data, error } = await supabase.from('semanas').insert({
      liga_id: ligaId, numero: proximoNumero, tipo,
      deadline: proximaQuinta.toISOString(),
      picks_abertos: true, spreads_congelados: false,
      resultados_processados: false, picks_revelados: false,
    }).select().single()

    if (error) throw error
    return { sucesso: true, semana: data }
  } catch (err) {
    return { sucesso: false, erro: err.message }
  }
}

// Busca todos os picks de uma semana já revelada, com o nome de quem escolheu.
// Usa ranking_liga (não membros_liga) pelo mesmo motivo de buscarGradePicks: contorna RLS.
export async function buscarPicksDaSemana(semanaId, ligaId) {
  try {
    const { data: picks } = await supabase
      .from('picks').select('membro_id, time_escolhido, vidas_antes, resultado').eq('semana_id', semanaId)

    const { data: membrosRanking } = await supabase
      .from('ranking_liga').select('membro_id, nome, vidas_restantes').eq('liga_id', ligaId)

    const nomePorMembro = {}
    for (const m of membrosRanking || []) nomePorMembro[m.membro_id] = m.nome

    const resultado = (picks || []).map(p => ({
      membro_id: p.membro_id,
      time_escolhido: p.time_escolhido,
      vidas_antes: p.vidas_antes,
      resultado: p.resultado,
      nome: nomePorMembro[p.membro_id] || 'Participante',
    }))

    return { sucesso: true, picks: resultado, membros: membrosRanking || [] }
  } catch (err) {
    return { sucesso: false, picks: [], membros: [], erro: err.message }
  }
}

export async function buscarGradePicks(ligaId) {
  try {
    const { data: semanas } = await supabase
      .from('semanas').select('id, numero, tipo, picks_revelados')
      .eq('liga_id', ligaId).order('numero', { ascending: true })

    // Usa ranking_liga como fonte de membros — a view já tem RLS correta
    // e expõe membro_id + nome para todos os participantes da liga
    const { data: membrosRanking } = await supabase
      .from('ranking_liga')
      .select('membro_id, nome, vidas_restantes, spread_medio, vidas_medias')
      .eq('liga_id', ligaId)

    const semanaIds = (semanas || []).map(s => s.id)

    const { data: picks } = semanaIds.length > 0
      ? await supabase
          .from('picks')
          .select('membro_id, semana_id, time_escolhido, resultado, spread_congelado')
          .in('semana_id', semanaIds)
      : { data: [] }

    // Monta mapa de semana_id -> picks_revelados para lookup rápido
    const semanaRevelada = {}
    for (const s of semanas || []) {
      semanaRevelada[s.id] = s.picks_revelados
    }

    // Inicializa grade com TODOS os membros da liga (mesmo quem não fez nenhum pick)
    const porMembro = {}
    for (const m of membrosRanking || []) {
      porMembro[m.membro_id] = {
        nome: m.nome,
        vidas: m.vidas_restantes ?? null,
        spreadMedio: m.spread_medio ?? null,
        vidasMedias: m.vidas_medias ?? m.vidas_restantes ?? null,
        picks: {},
      }
    }

    // Preenche picks de quem fez
    for (const pick of picks || []) {
      const mid = pick.membro_id
      if (!porMembro[mid]) continue
      const revelado = semanaRevelada[pick.semana_id]
      porMembro[mid].picks[pick.semana_id] = {
        time: revelado ? pick.time_escolhido : '?',
        resultado: revelado ? pick.resultado : null,
        spread: pick.spread_congelado,
      }
    }

    return { sucesso: true, semanas: semanas || [], grade: porMembro }
  } catch (err) {
    return { sucesso: false, semanas: [], grade: {} }
  }
}
